import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type {
  CmsPage,
  EventApplicationBulkMarkRequest,
  EventApplicationBulkMarkResponse,
} from '../types/cms';

export type ApplicationStatus = 'APPLIED' | 'DISQUALIFIED' | 'WINNER' | 'LOSER';

export interface EventApplication {
  applicationId: string;
  eventId: string;
  userId: string;
  userDisplayName?: string;
  orderId: string;
  paymentId?: string | null;
  productId: string;
  productName?: string;
  status: ApplicationStatus;
  disqualifyReason?: string | null;
  quantity: number;
  appliedAt: string;
}

export interface EventApplicationsQueryParams {
  status?: ApplicationStatus | '';
  page?: number;
  size?: number;
}

const BASE = '/admin/v1/events';

// be-catalog 확정: 응답은 envelope { data: EventApplicationView[], meta.pagination }.
interface ApplicationsEnvelope {
  data?: EventApplication[] | CmsPage<EventApplication> | null;
  meta?: {
    pagination?: { page: number; size: number; totalItems: number; totalPages: number };
  } | null;
}

export const eventApplicationsQueryKey = (eventId: string, params: EventApplicationsQueryParams) =>
  ['admin', 'event-applications', eventId, params] as const;

export const useEventApplicationsQuery = (
  eventId: string,
  params: EventApplicationsQueryParams = {},
) => {
  const { status, page = 0, size = 20 } = params;

  return useQuery<CmsPage<EventApplication>>({
    queryKey: eventApplicationsQueryKey(eventId, params),
    queryFn: async () => {
      // be-catalog 계약: keyword 파라미터 미지원 (status + page + size 만).
      const q = new URLSearchParams({ page: String(page), size: String(size) });
      if (status) q.set('status', status);

      const res = await adminFetch(`${BASE}/${eventId}/applications?${q}`);
      if (!res.ok) throw new Error(`applications fetch failed: ${res.status}`);
      const env = (await res.json()) as ApplicationsEnvelope;
      const raw = env.data;

      // 정본 경로: data=array + meta.pagination (총건수/페이지수는 meta 에서).
      if (Array.isArray(raw)) {
        const p = env.meta?.pagination;
        return {
          content: raw,
          totalElements: p?.totalItems ?? raw.length,
          totalPages: p?.totalPages ?? 1,
          number: p?.page ?? page,
          size: p?.size ?? size,
        };
      }
      // 방어: data 가 Page 객체로 올 경우.
      return raw ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size };
    },
    enabled: !!eventId,
  });
};

export const useEventApplicationDisqualifyMutation = (eventId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ applicationId, reason }: { applicationId: string; reason?: string }) => {
      const res = await adminFetch(
        `${BASE}/${eventId}/applications/${applicationId}/disqualify`,
        {
          method: 'PATCH',
          body: JSON.stringify({ reason: reason ?? null }),
        },
      );
      if (!res.ok) throw new Error(`disqualify failed: ${res.status}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'event-applications', eventId] });
    },
  });
};

export const useEventApplicationBulkMarkMutation = (eventId: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: EventApplicationBulkMarkRequest): Promise<EventApplicationBulkMarkResponse> => {
      const res = await adminFetch(`${BASE}/${eventId}/applications/bulk-mark`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`bulk-mark failed: ${res.status}`);
      const env = (await res.json()) as { data?: EventApplicationBulkMarkResponse };
      if (!env.data) throw new Error('no bulk-mark response data');
      return env.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'event-applications', eventId] });
    },
  });
};
